import { NETWORK_BY_FIELD, NETWORK_FIELD_ORDER, NETWORK_KEYS, NETWORK_LABELS, type NetworkKey } from "./networks";
import { PROFILE_THEME_IDS } from "./profile-themes";
export type ApiMeta = {
  page?: number;
  pageSize?: number;
  total?: number;
  totalPages?: number;
  trackingToken?: string;
};

export type ApiEnvelope<T> = { data: T; meta?: ApiMeta };

export class ApiError extends Error {
  code: string;
  status: number;
  details?: unknown;
  requestId?: string;

  constructor(message: string, options: { code?: string; status?: number; details?: unknown; requestId?: string } = {}) {
    super(message);
    this.name = "ApiError";
    this.code = options.code || "UNKNOWN_ERROR";
    this.status = options.status || 500;
    this.details = options.details;
    this.requestId = options.requestId;
  }
}

export type Category = {
  id: string;
  slug: string;
  name: string;
  description?: string;
  icon?: string;
  creatorCount: number;
};
export type Tag = { id: string; slug: string; name: string };
export type ProfileTheme = { preset?: string };

export type CreatorSummary = {
  id: string;
  slug: string;
  name: string;
  headline: string;
  category: string;
  categorySlug: string;
  city: string;
  state: string;
  score: number;
  avatarUrl?: string | null;
  isPremium?: boolean;
  isVerified?: boolean;
  position?: number;
  scoreChange?: number;
  tags?: string[];
  networks?: NetworkKey[];
  featuredCampaignId?: string;
  trafficSource?: "FEATURED" | "ORGANIC";
};

export type CreatorLink = {
  id: string;
  title: string;
  url: string;
  type?: "WEBSITE" | "SOCIAL" | "PORTFOLIO" | "OTHER";
  active?: boolean;
  clicks?: number;
  platform?: NetworkKey | "CUSTOM";
  icon?: string | null;
  isHighlighted?: boolean;
};

export type CreatorProfile = CreatorSummary & {
  bio: string;
  coverUrl?: string | null;
  links: CreatorLink[];
  updatedAt?: string;
  memberSince?: string;
  views?: number;
  showPublicViewCount?: boolean;
  scoreBreakdown?: { completeness: number; engagement: number; discovery: number; activity: number };
  canonicalSlug?: string;
  redirected?: boolean;
  related?: CreatorSummary[];
  themeJson?: ProfileTheme | null;
};

export type HomeData = {
  featured: CreatorSummary[];
  ranking: CreatorSummary[];
  rising: CreatorSummary[];
  newest: CreatorSummary[];
  categories: Category[];
  stats: { creators: number; cities: number; monthlyViews: number };
};

export type SearchFilters = {
  query?: string;
  category?: string;
  city?: string;
  state?: string;
  tag?: string;
  network?: "instagram" | "youtube" | "tiktok" | "twitch" | "x" | "linkedin" | "website";
  minScore?: number;
  premium?: boolean;
  sort?: string;
  page?: number;
};

export type SearchResult = { items: CreatorSummary[]; meta: Required<Pick<ApiMeta, "page" | "pageSize" | "total" | "totalPages">> & Pick<ApiMeta, "trackingToken"> };

export type ProfileDraft = {
  id?: string;
  status?: "DRAFT" | "PUBLISHED" | "SUSPENDED" | "REJECTED";
  name: string;
  username?: string;
  slug: string;
  headline: string;
  bio: string;
  categorySlug: string;
  city: string;
  state: string;
  avatarUrl?: string | null;
  instagramUrl?: string | null;
  youtubeUrl?: string | null;
  tiktokUrl?: string | null;
  twitchUrl?: string | null;
  xUrl?: string | null;
  linkedinUrl?: string | null;
  websiteUrl?: string | null;
  tagIds?: string[];
  showPublicViewCount?: boolean;
  publicUrl?: string | null;
  isPremium?: boolean;
  score?: number;
  themeJson?: ProfileTheme | null;
};

export type ReferralSummary = {
  referralUrl: string;
  qualifiedCount: number;
  target: number;
  rewardStatus: "LOCKED" | "PENDING" | "PROCESSING" | "APPLIED" | "GRANTED" | "FAILED" | "REVERSED";
};

export type Entitlements = {
  plan: "FREE" | "PREMIUM";
  source: "NONE" | "SUBSCRIPTION" | "TRIAL" | "REFERRAL_GRANT" | "ADMIN_GRANT" | "PROMOTION" | null;
  startsAt?: string | null;
  endsAt?: string | null;
  subscription?: {
    status: string;
    cancelAtPeriodEnd?: boolean;
    currentPeriodEnd?: string | null;
  } | null;
  trial?: { used: boolean; startsAt?: string | null; endsAt?: string | null };
};

export type DashboardOverview = {
  profile: ProfileDraft;
  entitlements: Entitlements;
  metrics: { views: number; clicks: number; shares: number; score: number; viewsChange: number; clicksChange: number };
  recentViews: Array<{ date: string; views: number; clicks: number }>;
  checklist: Array<{ id: string; label: string; done: boolean }>;
};

export type AnalyticsData = {
  summary: {
    views: number;
    uniqueViews: number;
    clicks: number;
    uniqueClicks: number;
    shares: number;
    searchImpressions: number;
    searchClicks: number;
    ctr: number;
  };
  comparison: Record<"views" | "clicks" | "shares" | "searches" | "searchClicks" | "ctr", { current: number; previous: number; changePercent: number }>;
  series: Array<{ date: string; views: number; clicks: number }>;
  sources: Array<{ name: string; value: number }>;
  topLinks: CreatorLink[];
  topTerms: Array<{ term: string; count: number }>;
  devices: Array<{ name: string; count: number }>;
};

export type ScoreData = {
  score: number;
  position: number | null;
  categoryPosition: number | null;
  categoryName: string | null;
  categorySlug: string | null;
  updatedAt: string;
  breakdown: Array<{ key: string; label: string; value: number; max: number; hint: string }>;
  history: Array<{ date: string; score: number }>;
};

export type FeaturedCampaign = {
  id: string;
  status: "SCHEDULED" | "ACTIVE" | "COMPLETED" | "CANCELED";
  startsAt: string;
  endsAt: string;
  impressions: number;
  clicks: number;
  origin?: "TRIAL" | "PURCHASE";
};

export type AdminOverview = {
  stats: { users: number; publishedProfiles: number; premiumUsers: number; openReports: number };
  reports: Array<{ id: string; reason: string; profileName: string; createdAt: string; status: string }>;
  recentUsers: Array<{ id: string; name: string; email: string; createdAt: string; status: string }>;
};

export type AdminProfile = {
  id: string;
  name: string;
  slug: string;
  status: string;
  isPublished: boolean;
  isVerified: boolean;
  isDiscoverable?: boolean;
  createdAt: string;
  user?: { id?: string; email?: string };
  category?: { name: string } | null;
  city?: { name: string } | null;
};

export type SeedIdentity = { firebaseUid: string; email: string };
export type CreateProfileInput = {
  name: string; username: string; bio: string; categorySlug: string; city: string; stateCode: string;
  firebaseUid: string; email: string; premium: boolean;
  avatarFile?: File | null; avatarSourceUrl?: string;
  networks: Record<string, string>;
};

export type BulkDeleteReport = {
  purged: number;
  anonymized: number;
  results: Array<{ userId: string; name: string; mode: "PURGED" | "ANONYMIZED" }>;
  failed: Array<{ userId: string; name: string; message: string }>;
};
export type BulkStatusReport = { updated: number; failed: Array<{ profileId: string; message: string }> };

export type AdminReport = {
  id: string;
  reason: string;
  details?: string | null;
  status: string;
  createdAt: string;
  profile?: { id: string; name: string; slug: string } | null;
  creatorProfile?: { id: string; name: string; slug: string } | null;
};

export type FeaturedPackage = { id: string; name: string; days: number; priceInCents: number; currency: string; isActive: boolean };
export type BillingPeriod = "MONTHLY" | "YEARLY";
export type PremiumPlan = {
  id: string | null;
  name: string;
  /** What Stripe charges per cycle — the full annual amount on a yearly plan. */
  priceInCents: number;
  currency: string;
  billingPeriod: BillingPeriod;
  /** priceInCents spread over the cycle, so the two plans compare on one axis. */
  monthlyEquivalentInCents: number;
  savingsPerYearInCents: number;
  savingsPercent: number;
  trialDays: number;
};
export type PricingCatalogue = {
  premium: { id: string | null; name: string; priceInCents: number; currency: string; billingPeriod: "MONTHLY"; trialDays: number };
  /** Optional: an API deployed before the annual plan does not send it. */
  premiumPlans?: PremiumPlan[];
  featured: FeaturedPackage[];
  notice: string;
};
export type PremiumPrice = { id: string; name: string; priceInCents: number; currency: string; billingPeriod: BillingPeriod; stripePriceId: string; isActive: boolean; createdAt: string; updatedAt: string };
export type AdminCategory = Category & { seoTitle?: string | null; seoDescription?: string | null; imageUrl?: string | null; parentId?: string | null; isActive: boolean; sortOrder: number };
export type ConsentRecord = { id: string; type: "ANALYTICS" | "COOKIES" | "MARKETING"; status: "GRANTED" | "REVOKED"; policyVersion: string; createdAt: string };
export type AdminUser = { id: string; name: string; email: string; role: "USER" | "ADMIN"; status: "ACTIVE" | "SUSPENDED" | "DELETED"; createdAt: string; profile?: { slug?: string; status?: string; isPremium?: boolean; isDiscoverable?: boolean } | null; featuredCampaigns?: Array<{ id: string; source: "ADMIN" | "PURCHASED" | "NEW_PROFILE_FREE"; status: string; startAt?: string | null; endAt?: string | null }> };
export type AdminPlanResult = { plan: "FREE" | "PREMIUM"; source: string; endsAt?: string | null; keptBySubscription: boolean };
export type AdminFeaturedResult = { featured: boolean; endsAt?: string | null; source: "ADMIN" | "PURCHASED" | "NEW_PROFILE_FREE" | null; keptByPurchase: boolean };
export type AdminCampaign = { id: string; status: string; startAt: string; endAt: string; impressions: number; clicks: number; createdAt: string; creatorProfile?: { name: string; slug: string } | null; featuredPackage?: { name: string; days: number } | null };
export type AdminJobSchedule = {
  enabled: boolean;
  tickMinutes: number;
  jobs: Array<{ name: string; label: string; intervalMinutes: number; lastRunAt: string | null; lastProcessedCount: number | null; nextRunAt: string; due: boolean }>;
};
export type AdminJobRun = { id: string; type: string; status: string; startedAt?: string | null; finishedAt?: string | null; processedCount: number; errorMessage?: string | null; createdAt: string };
export type AdminPayment = { id: string; type: string; status: string; amountInCents: number; currency: string; stripeInvoiceId?: string | null; createdAt: string };
export type AdminAnalytics = { periodDays: number; views: number; clicks: number; shares: number; searches: number; newUsers: number };
export type ApiKeyScope = "PROFILES_READ" | "PROFILES_WRITE";
export type AdminApiKey = {
  id: string;
  name: string;
  prefix: string;
  scopes: ApiKeyScope[];
  lastUsedAt?: string | null;
  requestCount: number;
  expiresAt?: string | null;
  revokedAt?: string | null;
  createdAt: string;
  createdBy?: { id: string; name: string; email: string } | null;
};
// The secret is returned by the creation call and never again.
export type AdminApiKeyCreated = AdminApiKey & { token: string };

export const isDemoMode = import.meta.env.VITE_DEMO_MODE === "true";

type AuthTokenProvider = () => Promise<string | null>;
let authTokenProvider: AuthTokenProvider = async () => null;
let developmentUserId: string | null = null;
let serverApiBase: string | null = null;

export function configureApiAuth(provider: AuthTokenProvider, devUserId?: string | null) {
  authTokenProvider = provider;
  developmentUserId = devUserId || null;
}

export function configureServerApiBase(base: string | null) {
  serverApiBase = base;
}

function getApiBase() {
  if (serverApiBase) return serverApiBase.replace(/\/$/, "");
  const configured = import.meta.env.VITE_API_URL;
  return (configured || "/api").replace(/\/$/, "");
}

async function request<T>(path: string, init: RequestInit = {}): Promise<ApiEnvelope<T>> {
  const token = typeof window !== "undefined" ? await authTokenProvider() : null;
  const headers = new Headers(init.headers);
  headers.set("Accept", "application/json");
  if (init.body && !(init.body instanceof FormData)) headers.set("Content-Type", "application/json");
  if (token) headers.set("Authorization", `Bearer ${token}`);
  if (!token && developmentUserId && import.meta.env.DEV) headers.set("X-Dev-User-Id", developmentUserId);

  let response: Response;
  try {
    response = await fetch(`${getApiBase()}${path}`, { ...init, headers, credentials: "include" });
  } catch (error) {
    throw new ApiError("Não foi possível conectar ao Score Creator.", { code: "NETWORK_ERROR", status: 0, details: error });
  }

  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new ApiError(payload?.error?.message || "Não foi possível concluir a solicitação.", {
      code: payload?.error?.code || "REQUEST_FAILED",
      status: response.status,
      details: payload?.error?.details,
      requestId: payload?.requestId,
    });
  }
  return payload as ApiEnvelope<T>;
}

function queryString(values: Record<string, string | number | undefined>) {
  const params = new URLSearchParams();
  Object.entries(values).forEach(([key, value]) => {
    if (value !== undefined && value !== "") params.set(key, String(value));
  });
  const string = params.toString();
  return string ? `?${string}` : "";
}

export const api = {
  async home() {
    const raw = (await request<Record<string, unknown>>("/public/home")).data;
    const featuredRows = Array.isArray(raw.featured) ? raw.featured.map((item: any) => item.creatorProfile ? { ...item.creatorProfile, featuredCampaignId: item.id, trafficSource: "FEATURED" } : item) : [];
    const rankingRows = Array.isArray(raw.ranking) ? raw.ranking : [];
    const growingRows = Array.isArray(raw.growing) ? raw.growing.map((item: any) => item.creatorProfile || item) : [];
    const newestRows = Array.isArray(raw.newest) ? raw.newest : [];
    const categories = Array.isArray(raw.categories) ? raw.categories.map(normalizeCategory) : [];
    const stats = (raw.stats && typeof raw.stats === "object" ? raw.stats : {}) as Record<string, unknown>;
    return {
      featured: featuredRows.map(normalizeCreator),
      ranking: rankingRows.map(normalizeCreator),
      rising: growingRows.map(normalizeCreator),
      newest: newestRows.map(normalizeCreator),
      categories,
      stats: { creators: Number(stats.creators ?? stats.publishedProfiles ?? rankingRows.length), cities: Number(stats.cities ?? 0), monthlyViews: Number(stats.monthlyViews ?? stats.views ?? 0) },
    } satisfies HomeData;
  },
  async categories() {
    return (await request<unknown[]>("/public/categories")).data.map(normalizeCategory);
  },
  async tags() {
    return (await request<Tag[]>("/public/tags")).data;
  },
  async search(filters: SearchFilters = {}): Promise<SearchResult> {
    const result = await request<unknown[]>(`/public/search${queryString({ q: filters.query, category: filters.category, city: filters.city, state: filters.state, tag: filters.tag, network: filters.network, minScore: filters.minScore, premium: filters.premium === undefined ? undefined : String(filters.premium), sort: mapSearchSort(filters.sort), page: filters.page })}`);
    const meta = result.meta || {};
    return {
      items: result.data.map(normalizeCreator),
      meta: {
        page: meta.page || 1,
        pageSize: meta.pageSize || result.data.length || 12,
        total: meta.total ?? result.data.length,
        totalPages: meta.totalPages || 1,
        trackingToken: meta.trackingToken,
      },
    };
  },
  async rankings(scope = "geral", category?: string, location: { state?: string; city?: string } = {}) {
    const type = ({ crescimento: "GROWING", semanal: "GROWING", novos: "NEW_CREATORS", acessados: "MOST_VIEWED", compartilhados: "MOST_SHARED" } as Record<string, string>)[scope] || (category ? "CATEGORY" : "GENERAL");
    return (await request<unknown[]>(`/public/rankings${queryString({ type, category, state: location.state, city: location.city })}`)).data.map(normalizeCreator);
  },
  async creator(slug: string): Promise<CreatorProfile> {
    const raw = (await request<Record<string, any>>(`/public/creators/${encodeURIComponent(slug)}`)).data;
    return { ...normalizePublicProfile(raw.profile || raw), canonicalSlug: raw.canonicalSlug, redirected: Boolean(raw.redirected), related: Array.isArray(raw.related) ? raw.related.map(normalizeCreator) : [] };
  },
  async validateReferral(code: string) {
    return (await request<{ valid: boolean; attributed?: boolean; firstTouchPreserved?: boolean }>(`/public/referrals/${encodeURIComponent(code)}`)).data;
  },
  async reportProfile(creatorProfileId: string, reason: string, details?: string) {
    return (await request<{ id: string; status: string }>("/public/reports", { method: "POST", body: JSON.stringify({ creatorProfileId, reason, details: details || undefined }) })).data;
  },
  async trackShare(creatorProfileId: string, platform: string, identity: { sessionId: string; visitorId: string; consent: true }, attribution: { featuredCampaignId?: string | null; searchEventId?: string | null; internal?: boolean } = {}) {
    return request<void>("/public/track/share", {
      method: "POST",
      body: JSON.stringify({ creatorProfileId, platform, trafficSource: attribution.featuredCampaignId ? "FEATURED" : attribution.searchEventId ? "SEARCH" : attribution.internal ? "INTERNAL_RECOMMENDATION" : "SHARED", featuredCampaignId: attribution.featuredCampaignId || null, searchEventId: attribution.searchEventId || null, ...identity }),
    });
  },
  async trackProfileView(slug: string, identity: { sessionId: string; visitorId: string; consent: true }, referrer?: string | null) {
    return request<void>("/public/track/profile-view", { method: "POST", body: JSON.stringify({ slug, trafficSource: "DIRECT", referrer: referrer || null, ...identity }) });
  },
  async trackInternalProfileView(slug: string, identity: { sessionId: string; visitorId: string; consent: true }, referrer?: string | null) {
    return request<void>("/public/track/profile-view", { method: "POST", body: JSON.stringify({ slug, trafficSource: "INTERNAL_RECOMMENDATION", referrer: referrer || null, ...identity }) });
  },
  async trackAttributedProfileView(slug: string, featuredCampaignId: string, identity: { sessionId: string; visitorId: string; consent: true }, referrer?: string | null) {
    return request<void>("/public/track/profile-view", { method: "POST", body: JSON.stringify({ slug, trafficSource: "FEATURED", featuredCampaignId, referrer: referrer || null, ...identity }) });
  },
  async trackSearchProfileView(slug: string, searchEventId: string, identity: { sessionId: string; visitorId: string; consent: true }, referrer?: string | null) {
    return request<void>("/public/track/profile-view", { method: "POST", body: JSON.stringify({ slug, trafficSource: "SEARCH", searchEventId, referrer: referrer || null, ...identity }) });
  },
  async trackLinkClick(creatorProfileId: string, creatorLinkId: string | null, platform: CreatorLink["platform"], identity: { sessionId: string; visitorId: string; consent: true }, attribution: { featuredCampaignId?: string | null; searchEventId?: string | null; internal?: boolean } = {}) {
    return request<void>("/public/track/link-click", { method: "POST", body: JSON.stringify({ creatorProfileId, creatorLinkId, platform: platform || "CUSTOM", trafficSource: attribution.featuredCampaignId ? "FEATURED" : attribution.searchEventId ? "SEARCH" : attribution.internal ? "INTERNAL_RECOMMENDATION" : "DIRECT", featuredCampaignId: attribution.featuredCampaignId || null, searchEventId: attribution.searchEventId || null, ...identity }) });
  },
  async trackFeaturedImpression(campaignId: string) {
    return request<void>("/public/track/featured-impression", { method: "POST", body: JSON.stringify({ campaignId }) });
  },
  async trackSearch(trackingToken: string, identity: { sessionId: string; visitorId: string; consent: true }) {
    return (await request<{ id?: string; searchEventId?: string }>("/public/track/search", { method: "POST", body: JSON.stringify({ trackingToken, ...identity }) })).data;
  },
  async trackSearchResultClick(searchEventId: string, creatorProfileId: string, identity: { sessionId: string; visitorId: string; consent: true }) {
    return request<void>("/public/track/search-result-click", { method: "POST", body: JSON.stringify({ searchEventId, creatorProfileId, ...identity }) });
  },
  async syncAuth(body: { acceptedTerms?: boolean; acceptedPrivacy?: boolean; name?: string }) {
    return (await request<{ user: { id: string; name?: string; email: string; role?: string; avatarUrl?: string | null }; profile?: ProfileDraft | null }>("/auth/sync", {
      method: "POST",
      body: JSON.stringify(body),
    })).data;
  },
  async myAccount() {
    return (await request<{
      acceptedTermsAt: string | null;
      acceptedPrivacyAt: string | null;
    }>("/me")).data;
  },
  async myProfile() {
    const result = (await request<{ profile: Record<string, any> | null; publicUrl?: string | null }>("/me/profile")).data;
    if (!result.profile) throw new ApiError("Perfil ainda não criado.", { code: "PROFILE_NOT_FOUND", status: 404 });
    return normalizeDraft(result.profile, result.publicUrl);
  },
  async uploadAvatar(file: File) {
    const form = new FormData();
    form.set("avatar", file);
    return (await request<{ avatarUrl: string }>("/me/avatar", { method: "POST", body: form })).data.avatarUrl;
  },
  async saveProfile(body: Partial<ProfileDraft>) {
    const headline = body.headline?.trim().replace(/[.!?]+$/, "");
    const rawBio = body.bio?.trim() || "";
    const combinedBio = headline && !rawBio.toLocaleLowerCase("pt-BR").startsWith(headline.toLocaleLowerCase("pt-BR")) ? `${headline}. ${rawBio}` : rawBio;
    const profilePayload: Record<string, unknown> = { ...body };
    for (const field of ["avatarUrl", ...NETWORK_FIELD_ORDER] as const) {
      const value = body[field];
      if (typeof value === "string") profilePayload[field] = value.trim() || null;
    }
    if (body.bio !== undefined || body.headline !== undefined) profilePayload.bio = combinedBio.slice(0, 500);
    if (body.username !== undefined || body.slug !== undefined) profilePayload.username = body.username || body.slug?.replace(/-/g, ".");
    if (body.state !== undefined) profilePayload.stateCode = body.state;
    let result: ApiEnvelope<any>;
    try {
      result = await request<any>("/me/profile", { method: "PUT", body: JSON.stringify(profilePayload) });
    } catch (error) {
      if (!(error instanceof ApiError) || error.status !== 404) throw error;
      result = await request<any>("/me/profile", { method: "POST", body: JSON.stringify(profilePayload) });
    }
    const data = result.data?.profile ? result.data : { profile: result.data, publicUrl: result.data?.publicUrl };
    return normalizeDraft(data.profile, data.publicUrl);
  },
  async publishProfile() {
    const profile = (await request<Record<string, any>>("/me/profile/publish", { method: "POST" })).data;
    return normalizeDraft(profile, profile.publicUrl);
  },
  async referrals() {
    return (await request<ReferralSummary>("/me/referrals")).data;
  },
  async entitlements() {
    return (await request<Entitlements>("/me/entitlements")).data;
  },
  async overview(): Promise<DashboardOverview> {
    const raw = (await request<Record<string, any>>("/me/dashboard")).data;
    const profile = raw.profile ? normalizeDraft(raw.profile, raw.publicUrl) : { ...emptyDraft };
    const values = raw.metrics || {};
    return {
      profile,
      entitlements: normalizeEntitlements(raw.entitlement),
      metrics: { views: values.views || 0, clicks: values.clicks || 0, shares: values.shares || 0, score: raw.profile?.creatorScore || 0, viewsChange: 0, clicksChange: 0 },
      recentViews: [],
      checklist: buildChecklist(raw.profile),
    } satisfies DashboardOverview;
  },
  async links() {
    return (await request<unknown[]>("/me/links")).data.map(normalizeLink);
  },
  async saveLinks(links: CreatorLink[]) {
    const existing = (await request<unknown[]>("/me/links")).data.map(normalizeLink);
    const incomingIds = new Set(links.filter((item) => !item.id.startsWith("new-")).map((item) => item.id));
    await Promise.all(existing.filter((item) => !incomingIds.has(item.id)).map((item) => request(`/me/links/${item.id}`, { method: "DELETE" })));
    const saved = await Promise.all(links.map(async (link, position) => {
      const payload = linkPayload(link, position);
      if (link.id.startsWith("new-")) return normalizeLink((await request<any>("/me/links", { method: "POST", body: JSON.stringify(payload) })).data);
      return normalizeLink((await request<any>(`/me/links/${link.id}`, { method: "PUT", body: JSON.stringify(payload) })).data);
    }));
    return saved;
  },
  async analytics(input: string | { period: string; from?: string; to?: string } = "30d") {
    const range = typeof input === "string" ? { period: input } : input;
    const days = range.period === "today" ? 1 : Number.parseInt(range.period, 10) || 30;
    const from = range.period === "custom" && range.from ? `${range.from}T00:00:00.000Z` : undefined;
    const to = range.period === "custom" && range.to ? `${range.to}T23:59:59.999Z` : undefined;
    return normalizeAnalytics((await request<Record<string, any>>(`/me/analytics${queryString({ days, from, to })}`)).data);
  },
  async score() {
    return normalizeScore((await request<Record<string, any>>("/me/score")).data);
  },
  async featured() {
    return (await request<Record<string, any>[]>("/me/featured-campaigns")).data.map(normalizeCampaign);
  },
  async featuredPackages() {
    return (await request<FeaturedPackage[]>("/public/featured-packages")).data;
  },
  async pricing() {
    return (await request<PricingCatalogue>("/public/pricing")).data;
  },
  async subscriptionCheckout(billingPeriod: BillingPeriod = "MONTHLY") {
    return (await request<{ url: string; checkoutUrl?: string; sessionId: string; billingPeriod?: BillingPeriod }>("/stripe/create-subscription-checkout", {
      method: "POST",
      body: JSON.stringify({ billingPeriod }),
    })).data;
  },
  async featuredCheckout(packageId: string) {
    return (await request<{ url: string; sessionId: string }>("/stripe/create-featured-checkout", {
      method: "POST",
      body: JSON.stringify({ packageId }),
    })).data;
  },
  async portal() {
    const data = (await request<{ url?: string; portalUrl?: string }>("/stripe/create-customer-portal", { method: "POST" })).data;
    return { url: data.url || data.portalUrl || "" };
  },
  async adminOverview() {
    const [overview, users, reports] = await Promise.all([
      request<Record<string, number>>("/admin/overview"),
      request<Record<string, any>[]>("/admin/users?pageSize=5"),
      request<Record<string, any>[]>("/admin/reports?pageSize=5").catch(() => ({ data: [] })),
    ]);
    return {
      stats: { users: overview.data.users || 0, publishedProfiles: overview.data.profiles || 0, premiumUsers: overview.data.subscribers || 0, openReports: overview.data.openReports || 0 },
      reports: reports.data.map((item) => ({ id: item.id, reason: item.reason, profileName: item.profile?.name || item.creatorProfile?.name || "Perfil", createdAt: item.createdAt, status: item.status })),
      recentUsers: users.data.map((item) => ({ id: item.id, name: item.name || "Sem nome", email: item.email, createdAt: item.createdAt, status: item.status })),
    } satisfies AdminOverview;
  },
  async moderateProfile(profileId: string, action: "suspend" | "reactivate" | "reject") {
    const status = action === "suspend" ? "SUSPENDED" : action === "reject" ? "REJECTED" : "PUBLISHED";
    return request(`/admin/profiles/${profileId}`, { method: "PATCH", body: JSON.stringify({ status }) });
  },
  async adminProfiles(filters: { q?: string; status?: string; page?: number } = {}) {
    const result = await request<AdminProfile[]>(`/admin/profiles${queryString(filters)}`);
    return { items: result.data, meta: result.meta };
  },
  async bulkModerateProfiles(profileIds: string[], action: "suspend" | "reactivate") {
    const body = { profileIds, status: action === "suspend" ? "SUSPENDED" : "PUBLISHED" };
    return (await request<BulkStatusReport>("/admin/profiles/bulk-status", { method: "POST", body: JSON.stringify(body) })).data;
  },
  async bulkDeleteAccounts(userIds: string[]) {
    const body = { userIds, confirmation: "EXCLUIR CONTAS" };
    return (await request<BulkDeleteReport>("/admin/accounts/bulk-delete", { method: "POST", body: JSON.stringify(body) })).data;
  },
  async adminSeedIdentity(username?: string) {
    return (await request<SeedIdentity>(`/admin/profiles/seed-identity${queryString({ username })}`)).data;
  },
  async createAdminProfile(input: CreateProfileInput) {
    const fields: Record<string, string> = {
      name: input.name, username: input.username, bio: input.bio, categorySlug: input.categorySlug,
      city: input.city, stateCode: input.stateCode, firebaseUid: input.firebaseUid, email: input.email,
      premium: String(input.premium),
      ...Object.fromEntries(Object.entries(input.networks).filter(([, value]) => value)),
    };
    // A picked file has to travel as multipart; a pasted address is small enough
    // to go as JSON and lets the server fetch it under its own SSRF guard.
    if (input.avatarFile) {
      const form = new FormData();
      Object.entries(fields).forEach(([key, value]) => form.set(key, value));
      form.set("avatar", input.avatarFile);
      return (await request<{ id: string; slug: string; publicUrl: string; premium: boolean }>("/admin/profiles", { method: "POST", body: form })).data;
    }
    return (await request<{ id: string; slug: string; publicUrl: string; premium: boolean }>("/admin/profiles", {
      method: "POST",
      body: JSON.stringify({ ...fields, premium: input.premium, avatarSourceUrl: input.avatarSourceUrl }),
    })).data;
  },
  async setAdminUserPremium(id: string, grant: boolean) {
    return (await request<AdminPlanResult>(`/admin/users/${id}/premium`, { method: "POST", body: JSON.stringify({ grant }) })).data;
  },
  async setAdminUserFeatured(id: string, grant: boolean, days?: number) {
    return (await request<AdminFeaturedResult>(`/admin/users/${id}/featured`, { method: "POST", body: JSON.stringify(grant ? { grant, days } : { grant }) })).data;
  },
  async setProfileDiscoverable(profileId: string, isDiscoverable: boolean) {
    return (await request<AdminProfile>(`/admin/profiles/${profileId}`, { method: "PATCH", body: JSON.stringify({ isDiscoverable }) })).data;
  },
  async adminUsers(filters: { q?: string; status?: string; page?: number } = {}) {
    const result = await request<AdminUser[]>(`/admin/users${queryString(filters)}`);
    return { items: result.data, meta: result.meta };
  },
  async updateAdminUser(id: string, body: { status?: "ACTIVE" | "SUSPENDED" | "DELETED"; role?: "USER" | "ADMIN" }) {
    return (await request<AdminUser>(`/admin/users/${id}`, { method: "PATCH", body: JSON.stringify(body) })).data;
  },
  async adminCampaigns(filters: { status?: string; page?: number } = {}) {
    const result = await request<AdminCampaign[]>(`/admin/campaigns${queryString(filters)}`);
    return { items: result.data, meta: result.meta };
  },
  async updateAdminCampaign(id: string, body: { status?: "ACTIVE" | "SCHEDULED" | "COMPLETED" | "CANCELLED"; endAt?: string }) {
    return (await request<AdminCampaign>(`/admin/campaigns/${id}`, { method: "PATCH", body: JSON.stringify(body) })).data;
  },
  async adminJobSchedule() {
    return (await request<AdminJobSchedule>("/admin/jobs/schedule")).data;
  },
  async adminJobs(filters: { status?: string; type?: string; page?: number } = {}) {
    const result = await request<AdminJobRun[]>(`/admin/jobs${queryString(filters)}`);
    return { items: result.data, meta: result.meta };
  },
  async runAdminJob(name: "scores" | "rankings" | "campaigns" | "snapshots" | "retention" | "referrals" | "all") {
    return (await request<Record<string, unknown>>(`/admin/jobs/${name}/run`, { method: "POST" })).data;
  },
  async adminPayments(page = 1) {
    const result = await request<AdminPayment[]>(`/admin/payments${queryString({ page })}`);
    return { items: result.data, meta: result.meta };
  },
  async adminAnalytics() {
    return (await request<AdminAnalytics>("/admin/analytics")).data;
  },
  async adminReports(filters: { status?: string; page?: number } = {}) {
    const result = await request<AdminReport[]>(`/admin/reports${queryString(filters)}`);
    return { items: result.data, meta: result.meta };
  },
  async resolveReport(id: string, body: { status: string; resolutionNote?: string; action?: string }) {
    return request(`/admin/reports/${id}`, { method: "PATCH", body: JSON.stringify(body) });
  },
  async adminCategories() {
    return (await request<AdminCategory[]>("/admin/categories")).data.map((item) => ({ ...item, creatorCount: item.creatorCount || 0 }));
  },
  async createAdminCategory(body: { name: string; slug: string; description?: string; isActive?: boolean; sortOrder?: number }) {
    return (await request<AdminCategory>("/admin/categories", { method: "POST", body: JSON.stringify(body) })).data;
  },
  async updateAdminCategory(id: string, body: Partial<Pick<AdminCategory, "name" | "slug" | "description" | "isActive" | "sortOrder">>) {
    return (await request<AdminCategory>(`/admin/categories/${id}`, { method: "PUT", body: JSON.stringify(body) })).data;
  },
  async adminApiKeys() {
    return (await request<AdminApiKey[]>("/admin/api-keys")).data;
  },
  async createAdminApiKey(body: { name: string; scopes: ApiKeyScope[]; expiresAt?: string | null }) {
    return (await request<AdminApiKeyCreated>("/admin/api-keys", { method: "POST", body: JSON.stringify(body) })).data;
  },
  async revokeAdminApiKey(id: string) {
    return (await request<AdminApiKey>(`/admin/api-keys/${id}/revoke`, { method: "POST" })).data;
  },
  async deleteAdminApiKey(id: string) {
    await request<null>(`/admin/api-keys/${id}`, { method: "DELETE" });
  },
  async adminPremiumPrices() {
    return (await request<PremiumPrice[]>("/admin/premium-prices")).data;
  },
  async createAdminPremiumPrice(body: { name: string; priceInCents: number; currency: string; stripePriceId: string; isActive: boolean }) {
    return (await request<PremiumPrice>("/admin/premium-prices", { method: "POST", body: JSON.stringify(body) })).data;
  },
  async activateAdminPremiumPrice(id: string) {
    return (await request<PremiumPrice>(`/admin/premium-prices/${id}`, { method: "PATCH", body: JSON.stringify({ activate: true }) })).data;
  },
  async saveConsent(type: "ANALYTICS" | "COOKIES" | "MARKETING", granted: boolean) {
    return request("/me/consents", { method: "POST", body: JSON.stringify({ type, granted, policyVersion: "1.0" }) });
  },
  async consents() {
    return (await request<ConsentRecord[]>("/me/consents")).data;
  },
  async exportAccount() {
    return (await request<Record<string, unknown>>("/me/export")).data;
  },
  async deleteAccount(confirmation: "EXCLUIR MINHA CONTA") {
    return (await request<{ deleted: boolean }>("/me/account", { method: "DELETE", body: JSON.stringify({ confirmation }) })).data;
  },
};

export const demoCreators: CreatorSummary[] = [
  { id: "1", slug: "ana-luz", name: "Ana Luz", headline: "Fotógrafa de histórias que parecem cinema", category: "Fotografia", categorySlug: "fotografia", city: "São Paulo", state: "SP", score: 94, isPremium: true, isVerified: true, position: 1, scoreChange: 3, tags: ["Retratos", "Lifestyle"] },
  { id: "2", slug: "leo-tavares", name: "Léo Tavares", headline: "Vídeos curtos, ideias grandes", category: "Vídeo", categorySlug: "video", city: "Recife", state: "PE", score: 91, isPremium: true, position: 2, scoreChange: 7, tags: ["Reels", "Direção"] },
  { id: "3", slug: "maju-cria", name: "Maju Cria", headline: "Design de marcas com sotaque brasileiro", category: "Design", categorySlug: "design", city: "Salvador", state: "BA", score: 88, isVerified: true, position: 3, scoreChange: 2, tags: ["Branding", "Ilustração"] },
  { id: "4", slug: "caio-na-cozinha", name: "Caio na Cozinha", headline: "Receitas possíveis para dias corridos", category: "Gastronomia", categorySlug: "gastronomia", city: "Belo Horizonte", state: "MG", score: 86, isPremium: false, position: 4, scoreChange: 5, tags: ["Receitas", "Casa"] },
  { id: "5", slug: "bia-em-movimento", name: "Bia em Movimento", headline: "Bem-estar sem fórmula pronta", category: "Bem-estar", categorySlug: "bem-estar", city: "Florianópolis", state: "SC", score: 84, isPremium: true, position: 5, scoreChange: 9, tags: ["Corrida", "Rotina"] },
  { id: "6", slug: "gui-explica", name: "Gui Explica", headline: "Tecnologia traduzida para a vida real", category: "Tecnologia", categorySlug: "tecnologia", city: "Curitiba", state: "PR", score: 82, isPremium: false, position: 6, scoreChange: 1, tags: ["IA", "Tutoriais"] },
  { id: "7", slug: "nanda-planeja", name: "Nanda Planeja", headline: "Finanças pessoais sem economês", category: "Finanças", categorySlug: "financas", city: "Brasília", state: "DF", score: 80, isPremium: true, position: 7, scoreChange: 4, tags: ["Organização", "Investimentos"] },
  { id: "8", slug: "pedro-no-controle", name: "Pedro no Controle", headline: "Games, cultura e boas histórias", category: "Games", categorySlug: "games", city: "Manaus", state: "AM", score: 78, isPremium: false, position: 8, scoreChange: 2, tags: ["Reviews", "Streaming"] },
  { id: "9", slug: "ju-ensina", name: "Ju Ensina", headline: "Educação prática para aprender todo dia", category: "Educação", categorySlug: "educacao", city: "Fortaleza", state: "CE", score: 76, isPremium: true, position: 9, scoreChange: 6, tags: ["Estudos", "Carreira"] },
  { id: "10", slug: "ravi-de-rua", name: "Ravi de Rua", headline: "Moda brasileira com identidade e movimento", category: "Moda", categorySlug: "moda", city: "Porto Alegre", state: "RS", score: 74, isPremium: false, position: 10, scoreChange: 1, tags: ["Estilo", "Tendências"] },
];

export const demoCategories: Category[] = [
  { id: "1", slug: "fotografia", name: "Fotografia", creatorCount: 428 },
  { id: "2", slug: "design", name: "Design", creatorCount: 376 },
  { id: "3", slug: "video", name: "Vídeo", creatorCount: 312 },
  { id: "4", slug: "gastronomia", name: "Gastronomia", creatorCount: 244 },
  { id: "5", slug: "bem-estar", name: "Bem-estar", creatorCount: 208 },
  { id: "6", slug: "tecnologia", name: "Tecnologia", creatorCount: 184 },
];

export const demoHome: HomeData = {
  featured: demoCreators.slice(0, 3),
  ranking: demoCreators,
  rising: demoCreators.slice(3),
  newest: demoCreators.slice().reverse(),
  categories: demoCategories,
  stats: { creators: 2318, cities: 186, monthlyViews: 847000 },
};

export function demoProfile(slug: string): CreatorProfile {
  const creator = demoCreators.find((item) => item.slug === slug) || demoCreators[0];
  return {
    ...creator,
    bio: "Crio conteúdo com intenção, repertório e um olhar muito brasileiro. Por aqui você encontra meus trabalhos, projetos recentes e os melhores caminhos para a gente criar junto.",
    links: [
      { id: "1", title: "Conheça meu portfólio", url: "https://example.com/portfolio", type: "PORTFOLIO", active: true },
      { id: "2", title: "Vamos trabalhar juntos", url: "https://example.com/contato", type: "WEBSITE", active: true },
      { id: "3", title: "Me acompanhe no Instagram", url: "https://instagram.com", type: "SOCIAL", active: true },
    ],
    views: 12480,
    memberSince: "2025-03-01T00:00:00.000Z",
    updatedAt: new Date().toISOString(),
    scoreBreakdown: { completeness: 28, engagement: 31, discovery: 24, activity: 9 },
  };
}

export async function withDemoFallback<T>(promise: Promise<T>, fallback: T): Promise<T> {
  try {
    return await promise;
  } catch (error) {
    if (isDemoMode) return fallback;
    throw error;
  }
}

const emptyDraft: ProfileDraft = { name: "", username: "", slug: "", headline: "", bio: "", categorySlug: "", city: "", state: "", status: "DRAFT", tagIds: [], showPublicViewCount: false };

function normalizeCategory(raw: any): Category {
  return { id: String(raw.id || raw.slug), slug: String(raw.slug || "categoria"), name: String(raw.name || "Categoria"), description: raw.description || undefined, creatorCount: Number(raw.creatorCount ?? raw._count?.profiles ?? 0) };
}

function normalizeCreator(raw: any): CreatorSummary {
  const category = typeof raw.category === "string" ? raw.category : raw.category?.name || "Criador";
  const categorySlug = raw.categorySlug || raw.category?.slug || "criadores";
  const city = typeof raw.city === "string" ? raw.city : raw.city?.name || "Brasil";
  const state = raw.state || raw.stateCode || raw.city?.stateCode || "BR";
  const bio = typeof raw.bio === "string" ? raw.bio : "";
  const declaredNetworks = Array.isArray(raw.networks) ? raw.networks.map((network: unknown) => String(network).toUpperCase()) : [];
  const networks = [
    ...NETWORK_FIELD_ORDER.filter((field) => raw[field]).map((field) => NETWORK_BY_FIELD[field]),
    ...declaredNetworks,
  ].filter((network, index, list): network is NetworkKey => NETWORK_KEYS.includes(network as NetworkKey) && list.indexOf(network) === index);
  return { id: String(raw.id), slug: String(raw.slug), name: String(raw.name || raw.username || "Criador"), headline: raw.headline || bio.split(/[.!?]/)[0] || `Criador de ${category}`, category, categorySlug, city, state, score: Number(raw.score ?? raw.creatorScore ?? 0), avatarUrl: raw.avatarUrl, isPremium: Boolean(raw.isPremium), isVerified: Boolean(raw.isVerified), position: raw.position ?? raw.rankGeneral ?? undefined, scoreChange: raw.scoreChange ?? 0, tags: Array.isArray(raw.tags) ? raw.tags.map((item: any) => item.tag?.name || item.name).filter(Boolean) : [], networks, featuredCampaignId: raw.featuredCampaignId, trafficSource: raw.trafficSource };
}

function normalizeLink(raw: any): CreatorLink {
  const platform = raw.platform || raw.type || "OTHER";
  return { id: String(raw.id), title: String(raw.title || "Link"), url: String(raw.url || ""), type: platform === "CUSTOM" ? "OTHER" : platform === "WEBSITE" ? "WEBSITE" : platform === "PORTFOLIO" ? "PORTFOLIO" : "SOCIAL", platform: ["INSTAGRAM", "YOUTUBE", "TIKTOK", "TWITCH", "X", "LINKEDIN", "WEBSITE", "CUSTOM"].includes(platform) ? platform : "CUSTOM", icon: raw.icon || null, isHighlighted: Boolean(raw.isHighlighted), active: raw.active ?? raw.isActive ?? true, clicks: Number(raw.clicks ?? raw.clickCount ?? 0) };
}

function normalizePublicProfile(raw: any): CreatorProfile {
  const creator = normalizeCreator(raw);
  const storedLinks = Array.isArray(raw.links) ? raw.links.map(normalizeLink) : [];
  const socialLinks = NETWORK_FIELD_ORDER
    .filter((field) => raw[field])
    .map((field) => ({ id: NETWORK_BY_FIELD[field].toLowerCase(), title: NETWORK_LABELS[NETWORK_BY_FIELD[field]], url: raw[field], platform: NETWORK_BY_FIELD[field] }))
    .map(normalizeLink);
  const links = [...storedLinks, ...socialLinks.filter((social) => !storedLinks.some((stored: CreatorLink) => stored.url === social.url))];
  return { ...creator, bio: raw.bio || creator.headline, links, updatedAt: raw.updatedAt, memberSince: raw.createdAt, views: raw.showPublicViewCount ? Number(raw.views ?? 0) : undefined, showPublicViewCount: Boolean(raw.showPublicViewCount), scoreBreakdown: raw.scoreBreakdown, themeJson: normalizeTheme(raw.themeJson) };
}

function normalizeDraft(raw: any, publicUrl?: string | null): ProfileDraft {
  const creator = normalizeCreator(raw);
  return { id: raw.id, status: raw.status || (raw.isPublished ? "PUBLISHED" : "DRAFT"), name: creator.name, username: raw.username || creator.slug.replace(/-/g, "."), slug: creator.slug, headline: raw.headline || creator.headline, bio: raw.bio || "", categorySlug: creator.categorySlug, city: creator.city === "Brasil" ? "" : creator.city, state: creator.state === "BR" ? "" : creator.state, avatarUrl: raw.avatarUrl, ...Object.fromEntries(NETWORK_FIELD_ORDER.map((field) => [field, raw[field] ?? null])), tagIds: Array.isArray(raw.tagIds) ? raw.tagIds : Array.isArray(raw.tags) ? raw.tags.map((item: any) => item.tagId || item.tag?.id).filter(Boolean) : [], showPublicViewCount: Boolean(raw.showPublicViewCount), publicUrl: publicUrl ?? raw.publicUrl ?? null, isPremium: raw.isPremium, score: creator.score, themeJson: normalizeTheme(raw.themeJson) };
}

function normalizeTheme(raw: unknown): ProfileTheme | null {
  if (!raw || typeof raw !== "object") return null;
  const preset = (raw as Record<string, unknown>).preset;
  return PROFILE_THEME_IDS.includes(String(preset)) ? { preset: preset as ProfileTheme["preset"] } : null;
}

function normalizeEntitlements(raw: any): Entitlements {
  if (!raw) return { plan: "FREE", source: "NONE", subscription: null };
  return { plan: raw.plan || "FREE", source: raw.source || "NONE", startsAt: raw.startsAt || null, endsAt: raw.endsAt || null, subscription: raw.subscription ? { status: raw.subscription.status || "INACTIVE", cancelAtPeriodEnd: Boolean(raw.subscription.cancelAtPeriodEnd), currentPeriodEnd: raw.subscription.currentPeriodEnd || null } : null, trial: raw.trial ? { used: Boolean(raw.trial.used), startsAt: raw.trial.startsAt || null, endsAt: raw.trial.endsAt || null } : undefined };
}

function buildChecklist(raw: any): DashboardOverview["checklist"] {
  return [{ id: "bio", label: "Conte sua história na bio", done: Boolean(raw?.bio && raw.bio.length >= 40) }, { id: "links", label: "Adicione pelo menos 3 links", done: (raw?.links?.length || 0) >= 3 }, { id: "share", label: "Compartilhe seu perfil", done: false }, { id: "avatar", label: "Use uma foto de perfil", done: Boolean(raw?.avatarUrl) }];
}

function linkPayload(link: CreatorLink, position: number) {
  const platform = link.platform || (link.type === "WEBSITE" || link.type === "PORTFOLIO" ? "WEBSITE" : "CUSTOM");
  return { title: link.title, url: link.url, icon: link.icon || null, platform, position, isActive: link.active !== false, isHighlighted: Boolean(link.isHighlighted) };
}

export function normalizeAnalytics(raw: any): AnalyticsData {
  const totals = raw.totals || {};
  const comparison = raw.comparison || {};
  const daily = Array.isArray(raw.daily) ? raw.daily : [];
  const sourceRows = Array.isArray(raw.byTrafficSource?.views) ? raw.byTrafficSource.views : [];
  const sourceCount = (item: any) => Number(item.count ?? item._count?._all ?? item._count ?? 0);
  const totalSource = sourceRows.reduce((sum: number, item: any) => sum + sourceCount(item), 0);
  const dailyByDate = new Map<string, { date: string; views: number; clicks: number }>();
  daily.forEach((item: any) => {
    const date = String(item.date).slice(0, 10);
    const current = dailyByDate.get(date) || { date, views: 0, clicks: 0 };
    current.views += Number(item.views ?? item.profileViews ?? 0);
    current.clicks += Number(item.clicks ?? item.linkClicks ?? 0);
    dailyByDate.set(date, current);
  });
  const comparisonValue = (key: string) => ({
    current: Number(comparison[key]?.current || 0),
    previous: Number(comparison[key]?.previous || 0),
    changePercent: Number(comparison[key]?.changePercent || 0),
  });
  const ctr = Number(totals.ctr || 0) * (Number(totals.ctr || 0) <= 1 ? 100 : 1);
  return {
    summary: {
      views: Number(totals.views || 0),
      uniqueViews: Number(totals.uniqueViews || 0),
      clicks: Number(totals.clicks || 0),
      uniqueClicks: Number(totals.uniqueClicks || 0),
      shares: Number(totals.shares || 0),
      searchImpressions: Number(totals.searchImpressions || 0),
      searchClicks: Number(totals.searchClicks || 0),
      ctr,
    },
    comparison: {
      views: comparisonValue("views"),
      clicks: comparisonValue("clicks"),
      shares: comparisonValue("shares"),
      searches: comparisonValue("searches"),
      searchClicks: comparisonValue("searchClicks"),
      ctr: comparisonValue("ctr"),
    },
    series: [...dailyByDate.values()].sort((left, right) => left.date.localeCompare(right.date)),
    sources: sourceRows.map((item: any) => ({ name: trafficSourceLabel(item.trafficSource), value: totalSource ? Math.round((sourceCount(item) / totalSource) * 100) : 0 })),
    topLinks: Array.isArray(raw.topLinks) ? raw.topLinks.map(normalizeLink) : [],
    topTerms: Array.isArray(raw.topTerms) ? raw.topTerms.map((item: any) => ({ term: String(item.term || ""), count: Number(item.count || 0) })) : [],
    devices: Array.isArray(raw.devices) ? raw.devices.map((item: any) => ({ name: deviceLabel(String(item.deviceType || "UNKNOWN")), count: Number(item.count || 0) })) : [],
  };
}

export function normalizeScore(raw: any): ScoreData {
  const current = raw.current || {};
  const total = Number(current.totalScore ?? current.score ?? 0);
  const position = current.rankGeneral == null ? null : Number(current.rankGeneral);
  const categoryPosition = current.rankCategory == null ? null : Number(current.rankCategory);
  const category = current.category && typeof current.category === "object" ? current.category : null;
  return { score: total, position, categoryPosition, categoryName: category?.name ? String(category.name) : null, categorySlug: category?.slug ? String(category.slug) : null, updatedAt: current.calculatedAt || new Date().toISOString(), breakdown: [{ key: "completeness", label: "Completude", value: Number(current.completenessScore || 0), max: 30, hint: "Complete todas as informações essenciais." }, { key: "engagement", label: "Engajamento", value: Number(current.engagementScore || 0), max: 35, hint: "Transforme visitas em ações nos seus links." }, { key: "discovery", label: "Descoberta", value: Number(current.discoveryScore || 0), max: 25, hint: "Mantenha sinais claros para ser encontrado." }, { key: "activity", label: "Atividade", value: Number(current.activityScore || 0), max: 10, hint: "Atualize e acompanhe seu perfil." }], history: Array.isArray(raw.history) ? raw.history.slice().reverse().map((item: any) => ({ date: item.calculatedAt || item.createdAt, score: Number(item.totalScore || 0) })) : [] };
}

function normalizeCampaign(raw: any): FeaturedCampaign {
  return { id: String(raw.id), status: raw.status === "CANCELLED" ? "CANCELED" : raw.status, startsAt: raw.startsAt || raw.startAt, endsAt: raw.endsAt || raw.endAt, impressions: Number(raw.impressions ?? raw.impressionCount ?? 0), clicks: Number(raw.clicks ?? raw.clickCount ?? 0), origin: raw.origin || (raw.isFreeTrialCampaign ? "TRIAL" : "PURCHASE") } as FeaturedCampaign;
}

function mapSearchSort(sort?: string) { return ({ recentes: "recent", relevancia: "relevance", score: "score", acessos: "views", compartilhados: "shares", crescimento: "growth" } as Record<string, string>)[sort || ""] || undefined; }
function trafficSourceLabel(source: string) { return ({ ORGANIC: "Busca orgânica", SEARCH: "Busca no Score Creator", FEATURED: "Destaque", DIRECT: "Link direto", SHARED: "Compartilhamentos", INTERNAL_RECOMMENDATION: "Recomendações" } as Record<string, string>)[source] || source; }
function deviceLabel(device: string) { return ({ MOBILE: "Celular", TABLET: "Tablet", DESKTOP: "Computador", BOT: "Robô", UNKNOWN: "Não identificado" } as Record<string, string>)[device] || device; }
